import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import API from "../services/api";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./schooldetails.css";

export default function SchoolDetails() {
  const { id } = useParams();
  const [school, setSchool] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // PDF target section
  const pdfRef = useRef(null);

  const loadSchool = async () => {
    const res = await API.get(`/schools/${id}/`);
    // your API returns { school: {...} }
    setSchool(res.data.school);
  };

  useEffect(() => {
    loadSchool();
  }, []);

  const toggleActive = async () => {
    await API.post(`/schools/${id}/toggle-status/`);
    loadSchool();
  };

  const markBilling = async () => {
    await API.post(`/schools/${id}/toggle-billing/`);
    loadSchool();
  };

  const deleteSchool = async () => {
    if (window.confirm("Are you sure you want to delete this school?")) {
      await API.delete(`/schools/${id}/delete/`);
      window.location.href = "/schools";
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied!");
    } catch (e) {
      alert("Copy failed");
    }
  };

  // ==============================
  // ⭐ DOWNLOAD AS PDF FUNCTION
  // ==============================
  const downloadPDF = async () => {
    const element = pdfRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save(`${school.name}_details.pdf`);
  };

  if (!school) return <p>Loading...</p>;

  return (
    <div className="details-page">
      <div className="details-header">
        <div>
          <span className="page-kicker">School Profile</span>
          <h2>{school.name}</h2>
          <p>{school.subdomain}.navith.cloud</p>
        </div>
        <span className={school.is_active ? "details-status active" : "details-status inactive"}>
          {school.is_active ? "Active" : "Inactive"}
        </span>
      </div>

      {/* BUTTON ROW */}
      <div className="btn-row">
        <button className="btn-edit" onClick={() => (window.location.href = `/schools/edit/${id}`)}>
          Edit
        </button>

        <button className={school.is_active ? "btn-disable" : "btn-enable"} onClick={toggleActive}>
          {school.is_active ? "Deactivate" : "Activate"}
        </button>

        <button className="btn-delete" onClick={deleteSchool}>
          Delete
        </button>

        <button className="btn-billing" onClick={markBilling}>
          Mark as {school.billing_status === "paid" ? "Pending" : "Paid"}
        </button>

        <button className="btn-pdf" onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      {/* ⭐ PDF CONTENT WRAPPER */}
      <div className="details-card" ref={pdfRef}>
        <h3>School Info</h3>
        <p><strong>Email:</strong> {school.contact_email}</p>
        <p><strong>Phone:</strong> {school.contact_phone}</p>
        <p><strong>Subdomain:</strong> {school.subdomain}.navith.cloud</p>
        <p><strong>Custom Domain:</strong> {school.custom_domain || "-"}</p>

        <h3>Admin Login</h3>
        <p><strong>User:</strong> {school.admin_username}</p>
        <p><strong>Email:</strong> {school.admin_email}</p>

        <div className="password-row">
          <p style={{ margin: 0 }}>
            <strong>Password:</strong>{" "}
            <span className="password-text">
              {showPassword ? school.admin_password : "••••••••"}
            </span>
          </p>

          <button className="btn-eye" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? "Hide" : "Show"}
          </button>

          <button
            className="btn-copy"
            onClick={() => copyToClipboard(school.admin_password || "")}
            disabled={!school.admin_password}
          >
            Copy
          </button>
        </div>

        <h3>Plan Details</h3>
        <p><strong>Plan:</strong> {school.plan?.name}</p>
        <p><strong>Expiry:</strong> {school.plan_expiry || "N/A"}</p>

        <h3>Billing</h3>
        <p><strong>Students:</strong> {school.total_students} / {school.max_students_allowed}</p>
        <p>
          <strong>Monthly Cost:</strong> ₹
          {Number(school.monthly_due_amount || 0).toFixed(2)}
        </p>
        <p><strong>Status:</strong> {school.billing_status}</p>

        <h3>System</h3>
        <p><strong>Academic Year:</strong> {school.academic_year}</p>
        <p><strong>Payment Mode:</strong> {school.payment_mode}</p>

        <h3>Created</h3>
        <p>{new Date(school.created_at).toLocaleString()}</p>
      </div>
    </div>
  );
}
